import React, { Suspense, lazy, useContext } from "react";
import type { EmojiClickData, Theme } from "emoji-picker-react";
import { Spinner } from "@interstate/components/Spinner";
import { ErrorBoundary } from "@coxauto-ui/core/errors";
import styled from "styled-components";
import { ConversationDetailsContext } from "../../context";

// Local theme constant to avoid runtime import dependency
const EMOJI_THEME = "auto" as Theme;

// Lazy load the emoji picker component
const LazyEmojiPickerReact = lazy(() => import("emoji-picker-react"));

const StyledEmojiPicker = styled.div<{ $width: number; $height: number }>`
  width: ${props => props.$width}px;
  height: ${props => props.$height}px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

let emojiPickerHasError = false;
const EmojiPickerFallback = () => {
  emojiPickerHasError = true;
  return null;
};

export interface EmojiPickerProps {
  /** Callback when emoji is selected */
  onEmojiClick: (emojiData: EmojiClickData) => void;
  /** Custom height for the picker (default: 400) */
  height?: number;
  /** Custom width for the picker (default: 350) */
  width?: number;
  /** Whether to enable lazy loading of emojis (default: true) */
  lazyLoadEmojis?: boolean;
  /** Test ID for the picker container */
  "data-testid"?: string;
}

export const EmojiPicker: React.FC<EmojiPickerProps> = ({
  onEmojiClick,
  height = 400,
  width = 350,
  lazyLoadEmojis = true,
  "data-testid": testId = "emoji-picker"
}) => {
  const context = useContext(ConversationDetailsContext);
  const { setEmojiPickerError } = context;

  return (
    <StyledEmojiPicker $height={height} $width={width} data-testid={testId}>
      <ErrorBoundary
        fallback={<EmojiPickerFallback />}
        onError={error => {
          // eslint-disable-next-line no-console
          console.error("Emoji picker runtime error:", error);
          emojiPickerHasError = true;
          setEmojiPickerError(true);
        }}
      >
        <Suspense fallback={<Spinner id="emoji-picker-loading-indicator" />}>
          <LazyEmojiPickerReact
            height={height}
            lazyLoadEmojis={lazyLoadEmojis}
            previewConfig={{ showPreview: false }}
            searchPlaceHolder="Search emoji..."
            theme={EMOJI_THEME}
            width={width}
            onEmojiClick={onEmojiClick}
          />
        </Suspense>
      </ErrorBoundary>
    </StyledEmojiPicker>
  );
};

export { emojiPickerHasError };
export default EmojiPicker;
